// User Activity Service for MVP - Local Storage based
export interface UserActivity {
  id: string;
  type: 'order' | 'mission' | 'promotion' | 'general';
  title: string;
  description: string;
  timestamp: Date;
  icon: string;
  metadata?: any;
}

export interface UserStats {
  total_orders: number;
  pending_orders: number;
  completed_missions: number;
  active_promotions: number;
  last_activity?: UserActivity;
}

class UserActivityService {
  private readonly STORAGE_KEY = 'pango_user_activities';
  private readonly STATS_KEY = 'pango_user_stats';

  // Save user activity
  saveActivity(activity: Omit<UserActivity, 'id' | 'timestamp'>): void {
    try {
      const newActivity: UserActivity = {
        ...activity,
        id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date()
      };

      const existingActivities = this.getActivities();
      const updatedActivities = [newActivity, ...existingActivities].slice(0, 50); // Keep last 50 activities
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedActivities));
      this.updateStats(newActivity);
    } catch (error) {
      // Silent fail for local storage errors
    }
  }

  // Get recent activities
  getActivities(limit: number = 10): UserActivity[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const activities: UserActivity[] = JSON.parse(stored);
      return activities
        .map(activity => ({
          ...activity,
          timestamp: new Date(activity.timestamp)
        }))
        .slice(0, limit);
    } catch (error) {
      return [];
    }
  }

  // Get user stats
  getStats(): UserStats {
    try {
      const stored = localStorage.getItem(this.STATS_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      // Silent fail
    }

    // Return default stats
    return {
      total_orders: 0,
      pending_orders: 0,
      completed_missions: 0,
      active_promotions: 0
    };
  }

  // Update stats based on activity
  private updateStats(activity: UserActivity): void {
    try {
      const currentStats = this.getStats();
      let updatedStats = { ...currentStats };

      switch (activity.type) {
        case 'order':
          if (activity.title.includes('xác nhận')) {
            updatedStats.total_orders = (updatedStats.total_orders || 0) + 1;
          } else if (activity.title.includes('chờ')) {
            updatedStats.pending_orders = (updatedStats.pending_orders || 0) + 1;
          }
          break;
        case 'mission':
          if (activity.title.includes('hoàn thành')) {
            updatedStats.completed_missions = (updatedStats.completed_missions || 0) + 1;
          }
          break;
        case 'promotion':
          if (activity.title.includes('mới')) {
            updatedStats.active_promotions = (updatedStats.active_promotions || 0) + 1;
          }
          break;
      }

      updatedStats.last_activity = activity;
      localStorage.setItem(this.STATS_KEY, JSON.stringify(updatedStats));
    } catch (error) {
      // Silent fail
    }
  }

  // Initialize with sample data for MVP
  initializeSampleData(): void {
    const existingActivities = this.getActivities();
    if (existingActivities.length > 0) return; // Already initialized

    const sampleActivities: Omit<UserActivity, 'id' | 'timestamp'>[] = [
      {
        type: 'order',
        title: 'Đơn hàng #JZ5L05MH đã được xác nhận',
        description: 'Đơn hàng đã được xác nhận và đang chuẩn bị giao',
        icon: 'shopping-cart'
      },
      {
        type: 'mission',
        title: 'Hoàn thành nhiệm vụ "Đánh giá sản phẩm"',
        description: 'Bạn đã nhận được 50 điểm thưởng',
        icon: 'target'
      },
      {
        type: 'promotion',
        title: 'Khuyến mãi mới: Giảm 20% Pepsi',
        description: 'Áp dụng cho đơn hàng từ 500k',
        icon: 'gift'
      }
    ];

    // Add sample activities with different timestamps
    sampleActivities.forEach((activity, index) => {
      const activityWithTime = {
        ...activity,
        id: `sample_${index}`,
        timestamp: new Date(Date.now() - (index + 1) * 2 * 60 * 60 * 1000) // 2, 4, 6 hours ago
      };
      
      const existingActivities = this.getActivities();
      const updatedActivities = [activityWithTime, ...existingActivities];
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedActivities));
    });

    // Initialize stats
    const initialStats: UserStats = {
      total_orders: 1,
      pending_orders: 0,
      completed_missions: 1,
      active_promotions: 1
    };
    localStorage.setItem(this.STATS_KEY, JSON.stringify(initialStats));
  }

  // Clear all data (for testing)
  clearAllData(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STATS_KEY);
  }
}

// Create and export singleton instance
export const userActivityService = new UserActivityService();
